import classNames from 'classnames'
import { useState } from 'react'
import Carousel from 'react-bootstrap/Carousel'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Image from 'react-bootstrap/Image'
import Row from 'react-bootstrap/Row'
import { useSearchParams } from 'react-router'
import { DefaultButton } from '../components/buttons/default-button.tsx'
import { FlexedBicepEmoji } from '../components/icons/flexed-bicep-emoji.tsx'
import { RepeatButtonEmoji } from '../components/icons/repeat-button-emoji.tsx'
import { typography } from '../main.css.ts'
import { useTheme } from '../utils/higher-order-components/with-theme'
import { getEnvironmentData } from '../utils/hooks/get-environment-data.ts'
import './login-page.css'

export const LoginPage = () => {
  const [interval, setInterval] = useState<number | null>(3000)
  const { theme } = useTheme()
  const environmentData = getEnvironmentData()
  const [searchParams] = useSearchParams()
  const redirectPath = searchParams.get('redirect_path')

  return <LoginPagePure theme={theme} interval={interval} setInterval={setInterval} environmentData={environmentData} redirectPath={redirectPath} />
}

export const LoginPagePure = ({
  theme,
  interval,
  setInterval,
  environmentData,
  redirectPath,
}: {
  theme: ReturnType<typeof useTheme>['theme']
  interval: number | null
  setInterval: (interval: number | null) => void
  environmentData: ReturnType<typeof getEnvironmentData>
  redirectPath: string | null
}) => {
  return (
    <div
      id={theme}
      className="flex-shrink-0 gradient"
      style={{ padding: '0 24px' }}
      onClick={() => setInterval(null)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          setInterval(null)
        }
      }}
      role="button"
      tabIndex={0}
    >
      <Carousel pause={false} wrap={false} interval={interval} variant={theme}>
        {[
          {
            title: 'Automated PR magic',
            description: (
              <>
                {environmentData.githubAppName} quickly generates PRs for code enhancements.
                <br />
                Watch your repo improve with the click of a button.
              </>
            ),
            imgSrc: theme === 'dark' ? 'pixee-bot-pr.png' : 'pixee-bot-pr-light.png',
            imgWidth: 719,
          },
          {
            title: 'Three approaches keep you covered',
            description: `${environmentData.githubAppName} routinely monitors, responds to your activity, and fixes external findings.`,
            element: (
              <Container className="slide-2-container">
                <Row className="pb-4">
                  <Col className="d-flex justify-content-start">
                    <p className={classNames(typography.h6, 'text-start', 'slide-text', 'text-white', 'mb-0')}>
                      <RepeatButtonEmoji /> Continuous improvement
                    </p>
                  </Col>
                  <Col className="d-flex justify-content-start">
                    <p className={classNames(typography.h6, 'text-start', 'slide-text', 'text-white', 'mb-0')}>
                      <FlexedBicepEmoji /> Pull request hardening
                    </p>
                  </Col>
                  <Col className="d-flex justify-content-start">
                    <p className={classNames(typography.h6, 'text-start', 'slide-text', 'text-white', 'mb-0')}>🧩️ Third-party remediation</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className={classNames(typography.paragraph, 'slide-text', 'text-start', 'text-white')}>
                      {environmentData.githubAppName} will assess the designated default branch weekly, giving you one recommendation at a time via
                      PR. Ready for the next thing sooner? Summon with <b>@pixeebot next</b> to get another recommendation.
                    </p>
                  </Col>
                  <Col>
                    <p className={classNames(typography.paragraph, 'slide-text', 'text-start', 'text-white')}>
                      Every time you submit code, {environmentData.githubAppName} analyzes it, identifies security improvements, and immediately
                      provides a PR to your PR. All you have to do is review and approve!
                    </p>
                  </Col>
                  <Col>
                    <p className={classNames(typography.paragraph, 'slide-text', 'text-start', 'text-white')}>
                      {environmentData.githubAppName} can even take on fixing the issues found by third-party code scanners, just as a member of your
                      product security team would do. Connect your tools and let {environmentData.githubAppName} send you PRs to resolve the findings.
                    </p>
                  </Col>
                </Row>
              </Container>
            ),
          },
          {
            title: 'Java deserialization hardening',
            description: 'Prevent Java deserialization exploits with automated hardening.',
            imgSrc: theme === 'dark' ? 'harden-java-deserialization.png' : 'harden-java-deserialization-light.png',
            imgWidth: 1082,
          },
          {
            title: 'Upgrade unsafe TLS versions',
            description: 'Harden SSL connections by automating TLS v1.2 upgrades.',
            imgSrc: theme === 'dark' ? 'upgrade-sslcontext-tls.png' : 'upgrade-sslcontext-tls-light.png',
            imgWidth: 1082,
          },
          {
            title: 'Prevent stack trace exposure',
            description: 'Secure error responses by eliminating stack trace exposure.',
            imgSrc: theme === 'dark' ? 'stack-trace-exposure.png' : 'stack-trace-exposure-light.png',
            imgWidth: 1082,
          },
          {
            title: 'Fix database resource leaks',
            description: 'Effortlessly fix potential database leaks for peak performance and security.',
            imgSrc: theme === 'dark' ? 'database-resource-leak.png' : 'database-resource-leak-light.png',
            imgWidth: 877,
          },
          {
            title: 'Protect against zip slip',
            description: 'Stop zip slip exploits with automated hardening.',
            imgSrc: theme === 'dark' ? 'harden-zip-entry-paths.png' : 'harden-zip-entry-paths-light.png',
            imgWidth: 1082,
          },
        ].map((item, index) => (
          <Carousel.Item key={index}>
            <div className="text-center carousel-item-wrapper">
              <p className="carousel-item-title slide-text">{item.title}</p>
              <p className="carousel-item-description slide-text">{item.description}</p>
              {item.imgSrc ? (
                <Image
                  src={item.imgSrc}
                  width={item.imgWidth}
                  fluid={true}
                  style={{ borderRadius: index === 1 ? undefined : '15px' }}
                  className={index === 1 ? undefined : 'shadow'}
                />
              ) : item.element ? (
                item.element
              ) : null}
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
      <div className="install-button-group">
        <div className="existing-users">
          <div className="col d-flex justify-content-center">
            <DefaultButton href={redirectPath ? `/api/auth/login?redirect_path=${redirectPath}` : '/api/auth/login'} openInNewTab={false}>
              Sign In
            </DefaultButton>
          </div>
        </div>
      </div>
    </div>
  )
}
